/* eslint-disable react/no-unstable-nested-components, max-lines */
import { useMemo, useState } from 'react';
import { Select } from '@nazka/nazka.mapframe.components';
import { createColumnHelper } from '@tanstack/react-table';
import {
  faEye,
  faEyeSlash,
  faPencil,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from '@utils/i18n/i18n.consumers';
import { useAdaptationStories } from '@services/query.service';
import { useDeleteStory, useUpdateStoryStatus } from '@services/mutation.service';

import AdminTable from '../AdminTable/AdminTable';
import AdminStatusBadge from '../AdminStatusBadge/AdminStatusBadge';
import AdminHazardTags from '../AdminHazardTags/AdminHazardTags';
import AdminActions from '../AdminActions/AdminActions';
import AdminConfirmationDialog from '../AdminConfirmationDialog/AdminConfirmationDialog';

import { STATUS_FILTER_OPTIONS } from '../admin.constants';

import './admin-stories.styl';

const columnHelper = createColumnHelper();

function AdminStories() {
  const [deleteStory, setDeleteStory] = useState(null);
  const [showPublishStoryWarning, setShowPublishStoryWarning] = useState(null);
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_OPTIONS[0]);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: stories } = useAdaptationStories();

  const updateStoryStatus = useUpdateStoryStatus();
  const deleteStoryMutation = useDeleteStory();

  const columns = [
    columnHelper.accessor('translations.en.title', {
      header: 'Title',
      cell: info => (
        <span className="story-title">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: info => <AdminStatusBadge status={info.getValue()?.toLowerCase()} />,
    }),
    columnHelper.accessor('createdAt', {
      header: 'Creation date',
      cell: (info) => {
        const date = info.getValue();
        return date ? new Date(date).toLocaleDateString('nl-BE') : '-';
      },
    }),
    columnHelper.accessor('hazardCategories', {
      header: 'Linked hazard',
      enableSorting: false,
      cell: info => <AdminHazardTags hazards={info.getValue()} />,
    }),
    columnHelper.accessor('linkedPlanTitle.en', {
      header: 'Linked plan',
      cell: info => (info.getValue() ? (
        <a className="admin-stories__link" href={`/admin/plans/${info.row.original.linkedPlan}`} target="_blank" rel="noopener noreferrer">
          {info.getValue()}
        </a>
      ) : '-'),
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      meta: { width: '200px' },
      cell: (info) => {
        const story = info.row.original;
        const isPublished = story.status?.toLowerCase() === 'published';

        return (
          <AdminActions
            actions={[
              {
                icon: isPublished ? faEyeSlash : faEye,
                title: isPublished ? t('Unpublish story') : t('Publish story'),
                onClick: async () => {
                  try {
                    await updateStoryStatus.mutateAsync({ id: story.id, status: isPublished ? 'unpublish' : 'publish' })
                  } catch (err) {
                    if (err.message === 'Plan is not published.') {
                      setShowPublishStoryWarning(story);
                    }
                  }
                },
              },
              {
                icon: faPencil,
                title: t('Edit story'),
                onClick: () => navigate(`/admin/stories/${story.id}`),
              },
              {
                icon: faTrash,
                title: t('Delete story'),
                onClick: () => setDeleteStory({ title: story.translations.en.title, id: story.id }),
                iconColor: 'red',
              }]}
          />
        );
      },
    }),
  ];

  const filteredData = useMemo(() => {
    if (!stories) {
      return [];
    }

    if (statusFilter?.id === 'all') {
      return stories;
    }

    return stories.filter(story => story.status?.toLowerCase() === statusFilter?.id.toLowerCase());
  }, [stories, statusFilter]);

  return (
    <div className="admin-stories">
      <h2>
        {t('Adaptation stories')}
      </h2>
      <div className="admin-stories__filter">
        <Select
          options={STATUS_FILTER_OPTIONS}
          value={statusFilter}
          onInputChange={value => setStatusFilter(value)}
          className="admin-stories__filter__status"
          width="180px"
        />
      </div>
      <AdminTable
        data={filteredData}
        columns={columns}
        initialSorting={[{ id: 'createdAt', desc: true }]}
      />
      {deleteStory && (
        <AdminConfirmationDialog
          title={t('Delete story')}
          helpText={t('Are you sure you want to delete {{title}}? This action can not be reversed.', { title: deleteStory.title })}
          cancelText={t('Cancel')}
          confirmText={t('Yes, delete')}
          onConfirm={async () => {
            await deleteStoryMutation.mutateAsync({ id: deleteStory.id });
            setDeleteStory(null);
          }}
          onCancel={() => {
            setDeleteStory(null);
          }}
          isPending={deleteStoryMutation.isPending}
        />
      )}
      {showPublishStoryWarning && (
        <AdminConfirmationDialog
          title={t('Warning')}
          helpText={t('The linked plan of the story you are trying to publish is not published yet. Do you want to publish both or keep working on the content?')}
          cancelText={t('Cancel')}
          confirmText={t('Yes, publish both')}
          onCancel={() => setShowPublishStoryWarning(null)}
          onConfirm={async () => {
            await updateStoryStatus.mutateAsync({ id: showPublishStoryWarning.id, status: 'publish', publishLinkedPlan: true });
            setShowPublishStoryWarning(null);
          }}
          isPending={updateStoryStatus.isPending}
          className="admin-stories__publish-story-warning"
        />
      )}
    </div>
  );
}

export default AdminStories;
